/**
 * Form ↔ wire conversion for an employee's terms — PURE, no React.
 *
 * The form speaks "HH:MM", hours, minutes and ₹ strings; the wire is minutes
 * after midnight, seconds and integer paise (digit strings). ₹ ↔ paise is
 * BigInt / string arithmetic, never a float. The server re-validates every
 * rule here (`EmploymentTermsInputSchema`) and more; {@link formProblems}
 * only gives the operator a clear message before the request is sent.
 */
import { hhmmToMinute, minuteToHhmm } from '../../call-pricing/onlineEarningForm';
import type { StatsRangeParams } from '../listenerStats';
import {
  EMPLOYMENT_MAX_OT_RATE_PAISE,
  EMPLOYMENT_MAX_SALARY_PAISE,
  EMPLOYMENT_REASON_MAX,
  EMPLOYMENT_REASON_MIN,
  type BreakMode,
  type DivisorMode,
  type HolidayPay,
  type OffDayRule,
  type OvertimePayMethod,
  type OvertimeTrigger,
  type PeriodCapUnit,
  type RoundDirection,
  type SalaryAttendanceMode,
  type SalaryModel,
  type ScheduleDay,
  type ScheduleType,
  type SettlementKind,
  type SettlementMode,
  type SettlementTrigger,
  type TermsInput,
  type TermsOut,
  type TermsState,
  type WorkTimeBasis,
} from './employmentTypes';

export { hhmmToMinute, minuteToHhmm };

export const WEEKDAYS: ReadonlyArray<{ weekday: number; short: string; long: string }> = [
  { weekday: 1, short: 'Mon', long: 'Monday' },
  { weekday: 2, short: 'Tue', long: 'Tuesday' },
  { weekday: 3, short: 'Wed', long: 'Wednesday' },
  { weekday: 4, short: 'Thu', long: 'Thursday' },
  { weekday: 5, short: 'Fri', long: 'Friday' },
  { weekday: 6, short: 'Sat', long: 'Saturday' },
  { weekday: 7, short: 'Sun', long: 'Sunday' },
];

export const weekdayName = (n: number, long = false): string => {
  const d = WEEKDAYS.find((w) => w.weekday === n);
  return d ? (long ? d.long : d.short) : String(n);
};

export interface ScheduleRow {
  weekday: number;
  working: boolean;
  /** "HH:MM" */
  start: string;
  /** "HH:MM" — equal to or before `start` means the shift crosses midnight. */
  end: string;
  breakMinutes: number;
}

export interface OvertimeForm {
  enabled: boolean;
  trigger: OvertimeTrigger;
  /** Hours (decimal) — only for `daily_threshold`. */
  thresholdHours: number | null;
  payMethod: OvertimePayMethod;
  /** ₹ per hour, typed. Only for `fixed_rate`. */
  rateRupees: string;
  /** 1.5 = 1.5× the derived hourly rate. Only for `multiplier`. */
  multiplier: number;
  divisorMode: DivisorMode;
  divisorHours: number | null;
  minMinutes: number;
  roundMinutes: number;
  roundDirection: RoundDirection;
  dailyCapHours: number | null;
  periodCapHours: number | null;
  periodCapUnit: PeriodCapUnit;
  approvalRequired: boolean;
  manualAdjustAllowed: boolean;
  /** Fixed-window only: presence outside the window counts (and can become overtime). */
  outsideWindowCounts: boolean;
}

export interface EmploymentFormValues {
  state: TermsState;
  timezone: string;
  /** "HH:MM" — when the employee's work day starts (00:00 normally). */
  dayStart: string;
  weekStartDay: number;
  schedule: ScheduleRow[];
  breakMode: BreakMode;
  workTimeBasis: WorkTimeBasis;
  scheduleType: ScheduleType;
  /** Required hours per working day (decimal) — flexible schedules only. */
  dailyTargetHours: number | null;
  /** Minutes of lateness still counted as present. */
  presentToleranceMinutes: number;
  salaryModel: SalaryModel;
  /** ₹ as typed ("25000" / "25000.50"). */
  salaryRupees: string;
  salaryAttendanceMode: SalaryAttendanceMode;
  /** No regular pay on a working day unless its target is met. */
  minimumCompletionRequired: boolean;
  overtime: OvertimeForm;
  offDayRule: OffDayRule;
  holidayPay: HolidayPay;
  holidayWorkRule: OffDayRule;
  settlementMode: SettlementMode;
  settlementTrigger: SettlementTrigger;
}

// ---------------------------------------------------------------------------
// Money (copied from ListenerEarningsCredit so the two never drift apart)
// ---------------------------------------------------------------------------

export type RupeesParse = { ok: true; paise: bigint } | { ok: false; error: string };

/** Typed rupees → integer paise by STRING arithmetic: "500.5" → 50050n. */
export function parseRupeesToPaise(input: string, maxPaise: bigint = EMPLOYMENT_MAX_SALARY_PAISE): RupeesParse {
  const s = (input ?? '').trim().replace(/^₹\s*/, '').replace(/,/g, '');
  if (s === '') return { ok: false, error: 'Enter an amount' };
  const m = /^(\d+)(?:\.(\d{1,2}))?$/.exec(s);
  if (!m) return { ok: false, error: 'Enter a valid amount in rupees, e.g. 500 or 500.50 (max 2 decimals)' };
  const [, whole = '0', frac = ''] = m;
  const paise = BigInt(whole) * 100n + BigInt(frac.padEnd(2, '0'));
  if (paise <= 0n) return { ok: false, error: 'Amount must be greater than ₹0' };
  if (paise > maxPaise) return { ok: false, error: `Amount can be at most ${formatPaiseExact(maxPaise)}` };
  return { ok: true, paise };
}

/** Exact "₹1,234.50" from paise (bigint or digit string) — two decimals, en-IN grouping. */
export function formatPaiseExact(paise: bigint | string | number): string {
  const p = typeof paise === 'bigint' ? paise : BigInt(paise);
  const neg = p < 0n;
  const abs = neg ? -p : p;
  const rupees = (abs / 100n).toString();
  const frac = (abs % 100n).toString().padStart(2, '0');
  const head = rupees.length > 3 ? rupees.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' : '';
  const tail = rupees.length > 3 ? rupees.slice(-3) : rupees;
  return `${neg ? '-' : ''}₹${head}${tail}.${frac}`;
}

/** Paise (digit string / number) → "₹1,234.50"; null / undefined / garbage → "—" (never ₹0). */
export function money(paise: string | number | bigint | null | undefined): string {
  if (paise === null || paise === undefined || paise === '') return '—';
  try {
    return formatPaiseExact(typeof paise === 'number' ? Math.trunc(paise) : paise);
  } catch {
    return '—';
  }
}

/** Paise digit string → "1234.50" for an input field; null → ''. */
export function paiseToRupeesInput(paise: string | number | null | undefined): string {
  if (paise === null || paise === undefined || paise === '') return '';
  const p = BigInt(typeof paise === 'number' ? Math.trunc(paise) : paise);
  return `${(p / 100n).toString()}.${(p % 100n).toString().padStart(2, '0')}`;
}

/** Sum of digit-string paise; null when ANY part is null (redacted). */
export function sumPaise(...parts: Array<string | null | undefined>): string | null {
  let total = 0n;
  for (const p of parts) {
    if (p === null || p === undefined) return null;
    total += BigInt(p);
  }
  return total.toString();
}

export function reasonProblem(reason: string): string | null {
  const r = reason.trim();
  if (r.length < EMPLOYMENT_REASON_MIN) return `Reason needs at least ${EMPLOYMENT_REASON_MIN} characters`;
  if (r.length > EMPLOYMENT_REASON_MAX) return `Keep the reason under ${EMPLOYMENT_REASON_MAX} characters`;
  return null;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export function defaultOvertime(): OvertimeForm {
  return {
    enabled: false,
    trigger: 'after_scheduled_hours',
    thresholdHours: null,
    payMethod: 'same_rate',
    rateRupees: '',
    multiplier: 1.5,
    divisorMode: 'period_scheduled_hours',
    divisorHours: null,
    minMinutes: 0,
    roundMinutes: 0,
    roundDirection: 'down',
    dailyCapHours: null,
    periodCapHours: null,
    periodCapUnit: 'month',
    approvalRequired: false,
    manualAdjustAllowed: true,
    outsideWindowCounts: false,
  };
}

/** Mon–Sat 09:00–18:00 with a 60-minute break, Sunday off. */
export function defaultSchedule(): ScheduleRow[] {
  return WEEKDAYS.map((d) => ({
    weekday: d.weekday,
    working: d.weekday !== 7,
    start: '09:00',
    end: '18:00',
    breakMinutes: 60,
  }));
}

export function defaultFormValues(timezone = 'Asia/Kolkata'): EmploymentFormValues {
  return {
    state: 'active',
    timezone,
    dayStart: '00:00',
    weekStartDay: 1,
    schedule: defaultSchedule(),
    breakMode: 'auto_deduct',
    workTimeBasis: 'livestream_calls',
    scheduleType: 'fixed_window',
    dailyTargetHours: null,
    presentToleranceMinutes: 5,
    salaryModel: 'hourly',
    salaryRupees: '',
    salaryAttendanceMode: 'prorated',
    minimumCompletionRequired: false,
    overtime: defaultOvertime(),
    offDayRule: 'unpaid',
    holidayPay: 'paid',
    holidayWorkRule: 'overtime',
    settlementMode: 'manual',
    settlementTrigger: 'end_of_day',
  };
}

// ---------------------------------------------------------------------------
// Conversions
// ---------------------------------------------------------------------------

const hoursToSeconds = (h: number | null | undefined): number | null =>
  h === null || h === undefined || !Number.isFinite(h) ? null : Math.round(h * 3600);
const secondsToHours = (s: number | null | undefined): number | null =>
  s === null || s === undefined ? null : Math.round((s / 3600) * 100) / 100;
const minutesToSeconds = (m: number | null | undefined): number => Math.max(0, Math.round((m ?? 0) * 60));
const secondsToMinutes = (s: number | null | undefined): number => Math.round((s ?? 0) / 60);

/** A stored version → form values (money fields blank when redacted). */
export function formFromTerms(t: TermsOut): EmploymentFormValues {
  const byDay = new Map(t.schedule.map((d) => [d.weekday, d]));
  const ot = t.overtime;
  return {
    state: t.state,
    timezone: t.timezone,
    dayStart: minuteToHhmm(t.dayStartMinute),
    weekStartDay: t.weekStartDay,
    schedule: WEEKDAYS.map((w) => {
      const d = byDay.get(w.weekday);
      return d
        ? { weekday: w.weekday, working: d.working, start: minuteToHhmm(d.startMinute), end: minuteToHhmm(d.endMinute), breakMinutes: d.breakMinutes }
        : { weekday: w.weekday, working: false, start: '09:00', end: '18:00', breakMinutes: 0 };
    }),
    breakMode: t.breakMode,
    workTimeBasis: t.workTimeBasis,
    scheduleType: t.scheduleType ?? 'fixed_window',
    dailyTargetHours: secondsToHours(t.dailyTargetSeconds),
    presentToleranceMinutes: secondsToMinutes(t.presentToleranceSeconds),
    salaryModel: t.salaryModel,
    salaryRupees: paiseToRupeesInput(t.salaryAmountPaise),
    salaryAttendanceMode: t.salaryAttendanceMode,
    minimumCompletionRequired: t.minimumCompletionRequired ?? false,
    overtime: {
      enabled: ot.enabled,
      trigger: ot.trigger === 'disabled' ? 'after_scheduled_hours' : ot.trigger,
      thresholdHours: secondsToHours(ot.thresholdSeconds),
      payMethod: ot.payMethod,
      rateRupees: paiseToRupeesInput(ot.ratePaisePerHour),
      multiplier: ot.multiplierBps === null ? 1.5 : ot.multiplierBps / 10_000,
      divisorMode: ot.divisorMode,
      divisorHours: ot.divisorHours,
      minMinutes: secondsToMinutes(ot.minSeconds),
      roundMinutes: secondsToMinutes(ot.roundSeconds),
      roundDirection: ot.roundDirection,
      dailyCapHours: secondsToHours(ot.dailyCapSeconds),
      periodCapHours: secondsToHours(ot.periodCapSeconds),
      periodCapUnit: ot.periodCapUnit,
      approvalRequired: ot.approvalRequired,
      manualAdjustAllowed: ot.manualAdjustAllowed,
      outsideWindowCounts: ot.outsideWindowCounts ?? false,
    },
    offDayRule: t.offDayRule,
    holidayPay: t.holidayPay,
    holidayWorkRule: t.holidayWorkRule,
    settlementMode: t.settlementMode ?? 'manual',
    settlementTrigger: t.settlementTrigger ?? 'end_of_day',
  };
}

export function scheduleToWire(rows: ScheduleRow[]): ScheduleDay[] {
  return rows.map((r) => ({
    weekday: r.weekday,
    working: r.working,
    startMinute: hhmmToMinute(r.start),
    endMinute: hhmmToMinute(r.end),
    breakMinutes: Math.max(0, Math.round(r.breakMinutes || 0)),
  }));
}

/**
 * Form values → the `terms` body. Call {@link formProblems} first: an
 * unparseable salary becomes "0" here (which the server rejects).
 */
export function termsFromForm(f: EmploymentFormValues): TermsInput {
  const salary = parseRupeesToPaise(f.salaryRupees);
  const rate = parseRupeesToPaise(f.overtime.rateRupees, EMPLOYMENT_MAX_OT_RATE_PAISE);
  const ot = f.overtime;
  const fixedWindow = f.scheduleType === 'fixed_window';
  return {
    state: f.state,
    timezone: f.timezone.trim(),
    dayStartMinute: hhmmToMinute(f.dayStart),
    weekStartDay: f.weekStartDay,
    schedule: scheduleToWire(f.schedule),
    breakMode: f.breakMode,
    workTimeBasis: f.workTimeBasis,
    scheduleType: f.scheduleType,
    dailyTargetSeconds: fixedWindow ? null : hoursToSeconds(f.dailyTargetHours),
    presentToleranceSeconds: minutesToSeconds(f.presentToleranceMinutes),
    salaryModel: f.salaryModel,
    salaryAmountPaise: salary.ok ? salary.paise.toString() : '0',
    salaryAttendanceMode: f.salaryModel === 'hourly' ? 'prorated' : f.salaryAttendanceMode,
    minimumCompletionRequired: f.minimumCompletionRequired,
    overtime: {
      enabled: ot.enabled,
      trigger: ot.enabled ? ot.trigger : 'disabled',
      thresholdSeconds: ot.enabled && ot.trigger === 'daily_threshold' ? hoursToSeconds(ot.thresholdHours) : null,
      payMethod: ot.payMethod,
      // Never both: the method not in use goes as 0 / the default so the
      // server cannot pick up a stale value.
      ratePaisePerHour: ot.payMethod === 'fixed_rate' && rate.ok ? Number(rate.paise) : 0,
      multiplierBps: ot.payMethod === 'multiplier' ? Math.round((ot.multiplier || 0) * 10_000) : 15_000,
      divisorMode: ot.divisorMode,
      divisorHours: ot.divisorMode === 'fixed_hours' ? ot.divisorHours : null,
      minSeconds: minutesToSeconds(ot.minMinutes),
      roundSeconds: minutesToSeconds(ot.roundMinutes),
      roundDirection: ot.roundDirection,
      dailyCapSeconds: hoursToSeconds(ot.dailyCapHours),
      periodCapSeconds: hoursToSeconds(ot.periodCapHours),
      periodCapUnit: ot.periodCapUnit,
      approvalRequired: ot.approvalRequired,
      manualAdjustAllowed: ot.manualAdjustAllowed,
      // Only meaningful with a shift window: a flexible schedule has no "outside".
      ...(fixedWindow ? { outsideWindowCounts: ot.outsideWindowCounts } : {}),
    },
    offDayRule: f.offDayRule,
    holidayPay: f.holidayPay,
    holidayWorkRule: f.holidayWorkRule,
    settlementMode: f.settlementMode,
    settlementTrigger: f.settlementTrigger,
  };
}

// ---------------------------------------------------------------------------
// Derived figures + validation
// ---------------------------------------------------------------------------

/** Shift length in minutes (midnight crossing allowed). */
export function shiftSpanMinutes(r: { start: string; end: string }): number {
  const s = hhmmToMinute(r.start);
  const e = hhmmToMinute(r.end);
  return e > s ? e - s : 1440 - s + e;
}

/** Net scheduled seconds of one row (0 for a day off). */
export function rowNetSeconds(r: ScheduleRow): number {
  if (!r.working) return 0;
  return Math.max(0, (shiftSpanMinutes(r) - (r.breakMinutes || 0)) * 60);
}

/** Net scheduled seconds per week. */
export function weeklyScheduledSeconds(rows: ScheduleRow[]): number {
  return rows.reduce((s, r) => s + rowNetSeconds(r), 0);
}

/** Longest scheduled (net) day, seconds. */
export function longestDaySeconds(rows: ScheduleRow[]): number {
  return rows.reduce((m, r) => Math.max(m, rowNetSeconds(r)), 0);
}

/** Seconds a flexible schedule expects per working day (0 when unset). */
export function dailyTargetSecondsOf(f: Pick<EmploymentFormValues, 'scheduleType' | 'dailyTargetHours'>): number {
  return f.scheduleType === 'flexible' ? (hoursToSeconds(f.dailyTargetHours) ?? 0) : 0;
}

/** Net scheduled seconds per week for the form's schedule type. */
export function weeklyScheduledSecondsOf(f: Pick<EmploymentFormValues, 'schedule' | 'scheduleType' | 'dailyTargetHours'>): number {
  if (f.scheduleType === 'flexible') return f.schedule.filter((r) => r.working).length * dailyTargetSecondsOf(f);
  return weeklyScheduledSeconds(f.schedule);
}

/** Longest expected day for the form's schedule type. */
export function longestDaySecondsOf(f: Pick<EmploymentFormValues, 'schedule' | 'scheduleType' | 'dailyTargetHours'>): number {
  if (f.scheduleType === 'flexible') return f.schedule.some((r) => r.working) ? dailyTargetSecondsOf(f) : 0;
  return longestDaySeconds(f.schedule);
}

/** Problems the operator must fix before Save — each a full sentence. */
export function formProblems(f: EmploymentFormValues): string[] {
  const out: string[] = [];
  if (!f.timezone.trim()) out.push('Pick a timezone.');
  const dayStart = hhmmToMinute(f.dayStart);
  const flexible = f.scheduleType === 'flexible';
  let anyWorking = false;
  for (const r of f.schedule) {
    if (!r.working) continue;
    anyWorking = true;
    // A flexible day has no window: only the daily target matters.
    if (flexible) continue;
    const name = weekdayName(r.weekday, true);
    if (hhmmToMinute(r.start) === hhmmToMinute(r.end)) {
      out.push(`${name}: the shift end must differ from its start.`);
      continue;
    }
    const span = shiftSpanMinutes(r);
    if ((r.breakMinutes || 0) >= span) out.push(`${name}: the break must be shorter than the shift.`);
    if ((hhmmToMinute(r.start) - dayStart + 1440) % 1440 + span > 1440) {
      out.push(`${name}: the shift must fit inside one work day — move the work-day start earlier.`);
    }
  }
  if (!anyWorking) out.push('Mark at least one day as a working day.');
  if (flexible) {
    const target = hoursToSeconds(f.dailyTargetHours);
    if (target === null || target <= 0) out.push('Flexible schedule: set the required hours per day.');
    else if (target < 60) out.push('Flexible schedule: the required hours per day must be at least one minute.');
    else if (target > 86_400) out.push('Flexible schedule: the required hours per day cannot exceed 24.');
  }

  const salary = parseRupeesToPaise(f.salaryRupees);
  if (!salary.ok) out.push(`Salary: ${salary.error.toLowerCase()}.`);
  if (f.salaryModel === 'hourly' && f.salaryAttendanceMode === 'fixed') {
    out.push('Hourly pay is always by hours worked — pick "Prorated".');
  }
  if (f.presentToleranceMinutes < 0 || f.presentToleranceMinutes > 60) out.push('Present tolerance must be 0–60 minutes.');

  const ot = f.overtime;
  if (ot.enabled) {
    if (ot.trigger === 'daily_threshold') {
      const th = hoursToSeconds(ot.thresholdHours);
      if (th === null || th <= 0) out.push('Overtime: set the daily threshold (hours).');
      else if (th < longestDaySecondsOf(f)) {
        out.push('Overtime: the daily threshold must be at least the longest scheduled day.');
      }
    }
    if (ot.payMethod === 'fixed_rate') {
      const rate = parseRupeesToPaise(ot.rateRupees, EMPLOYMENT_MAX_OT_RATE_PAISE);
      if (!rate.ok) out.push(`Overtime rate: ${rate.error.toLowerCase()}.`);
    } else if (ot.payMethod === 'multiplier') {
      if (!(ot.multiplier > 0) || ot.multiplier > 10) out.push('Overtime multiplier must be between 0.1× and 10×.');
      if (ot.divisorMode === 'fixed_hours' && !(ot.divisorHours && ot.divisorHours >= 1 && ot.divisorHours <= 744)) {
        out.push('Overtime: enter the fixed divisor hours (1–744).');
      }
    }
    if (ot.minMinutes < 0) out.push('Overtime minimum minutes cannot be negative.');
    if (ot.roundMinutes < 0) out.push('Overtime rounding interval cannot be negative.');
  }
  return out;
}

// ---------------------------------------------------------------------------
// Dates (the employee's work dates are plain YYYY-MM-DD, never ISO instants)
// ---------------------------------------------------------------------------

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Local calendar date of a Date (browser zone) as YYYY-MM-DD. */
export function localDay(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** YYYY-MM-DD ± n days (calendar arithmetic, no zone issues). */
export function addDaysStr(day: string, n: number): string {
  const { y, m, d } = splitDay(day);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** YYYY-MM-DD → its parts (NaN-safe: a malformed string becomes 1970-01-01). */
export function splitDay(day: string): { y: number; m: number; d: number } {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  return m ? { y: Number(m[1]), m: Number(m[2]), d: Number(m[3]) } : { y: 1970, m: 1, d: 1 };
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** YYYY-MM-DD → "05 Oct 2026" (a work date, never converted through a zone). */
export function workDateLabel(day: string | null | undefined): string {
  if (!day || !/^\d{4}-\d{2}-\d{2}$/.test(day)) return day || '—';
  const { y, m, d } = splitDay(day);
  return `${pad2(d)} ${MONTHS[m - 1] ?? m} ${y}`;
}

export const todayStr = (): string => localDay(new Date());
export const tomorrowStr = (): string => addDaysStr(todayStr(), 1);

/**
 * The stats pickers give ISO instants (`to` exclusive midnight). The employment
 * API wants inclusive work dates, so: local day of `from`, local day of the
 * instant just before `to`.
 */
export function workDateRange(range: StatsRangeParams): { from: string; to: string } {
  return {
    from: localDay(new Date(range.from)),
    to: localDay(new Date(new Date(range.to).getTime() - 1)),
  };
}

// ---------------------------------------------------------------------------
// Labels used by the tab and the CSVs
// ---------------------------------------------------------------------------

/**
 * What counts as work. The first three are the normal choices; the `advanced`
 * ones are the older heartbeat-based bases (app-open / callable time).
 */
export const BASIS_OPTIONS: ReadonlyArray<{ value: WorkTimeBasis; label: string; hint: string; advanced?: boolean }> = [
  { value: 'livestream', label: 'Live time', hint: "The listener's own live streams." },
  { value: 'calls', label: 'Call time', hint: 'Connected audio / video calls only.' },
  { value: 'livestream_calls', label: 'Live + Calls', hint: 'Live streams and connected calls — overlaps counted once. (Default)' },
  { value: 'live', label: 'App-open time', hint: 'Heartbeat — the app is open on screen.', advanced: true },
  { value: 'online', label: 'Callable time', hint: 'Online incl. the reachability lease after the phone locks.', advanced: true },
  { value: 'live_calls', label: 'App-open + Calls', hint: 'Union of app-open time and call time — overlaps counted once.', advanced: true },
  { value: 'online_calls', label: 'Callable + Calls', hint: 'Union of callable time and call time — overlaps counted once.', advanced: true },
];

export const basisLabel = (basis: string | null | undefined): string =>
  BASIS_OPTIONS.find((b) => b.value === basis)?.label ?? (basis ? basis.replace(/_/g, ' ') : '—');

/** Hourly first: it is the default earning rate. */
export const SALARY_MODEL_LABEL: Record<SalaryModel, string> = {
  hourly: 'Hourly',
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
};

/** "₹ per hour" / "₹ per month" … */
export const salaryUnit = (model: SalaryModel): string => `₹ per ${model === 'hourly' ? 'hour' : model.replace(/ly$/, '')}`;

export const SCHEDULE_TYPE_LABEL: Record<ScheduleType, string> = {
  fixed_window: 'Fixed time window',
  flexible: 'Flexible',
};

export const SETTLEMENT_MODE_LABEL: Record<SettlementMode, string> = {
  automatic: 'Automatic',
  manual: 'Manual',
};

export const SETTLEMENT_TRIGGER_LABEL: Record<SettlementTrigger, string> = {
  end_of_day: 'End of work day',
  target_completed: 'When the daily target is completed',
};

export const SETTLEMENT_KIND_LABEL: Record<SettlementKind, string> = {
  daily: 'Daily',
  on_target: 'On target',
  adjustment: 'Adjustment',
};

export const OT_STATUS_LABEL: Record<string, string> = {
  none: 'None',
  auto: 'Auto',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

/** One sentence explaining the chosen attendance rule for the chosen frequency. */
export function attendanceRuleSentence(model: SalaryModel, mode: SalaryAttendanceMode): string {
  if (model === 'hourly') return 'Hourly: the employee is paid the rate for every hour actually worked.';
  const period = model === 'monthly' ? 'month' : model === 'weekly' ? 'week' : 'day';
  if (mode === 'fixed') {
    return `Fixed: the full ${period === 'day' ? 'daily' : period === 'week' ? 'weekly' : 'monthly'} amount is paid whatever the attendance; missing time is only reported.`;
  }
  return `Prorated: the ${period}'s amount is split over the scheduled hours — missing time reduces pay, a paid holiday counts as worked.`;
}
